// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useCallback, useEffect, useState } from "react"
import type { InsightsResponse } from "@bitebalance/contracts"
import { useApiClient } from "@/api/useApiClient"
import { useI18n } from "@/i18n"
import { LoadingState, ErrorState } from "@/components/state"
import { ProgressSection } from "@/components/insights/ProgressSection"
import { BudgetForecastBlock } from "@/components/insights/BudgetForecastBlock"
import { InsightsDetail, InsightsHeadline } from "@/components/insights/InsightsHeadline"

type LoadStatus = "loading" | "success" | "error"

/**
 * A7 fix (audit-2026-08-26): this page was a stub — only the ADR-027
 * Progress section (weigh-ins/trend) rendered; the realized/projected
 * value + adherence data mobile's Insights tab already has was missing
 * entirely. Ports mobile's GET /v1/insights/value rendering (ValueCard,
 * shared with TodayPage.tsx) in above ProgressSection. ValueCard itself
 * preserves the honest empty state (never €0-as-certainty) when there's
 * truly no data yet; this page's own loading/error states cover the
 * insights fetch, matching TodayPage's pattern.
 */
export default function InsightsPage() {
  const { t } = useI18n()
  const api = useApiClient()
  const [status, setStatus] = useState<LoadStatus>("loading")
  const [insights, setInsights] = useState<InsightsResponse | null>(null)

  const load = useCallback(() => {
    setStatus("loading")
    api
      .getInsightsValue()
      .then(result => {
        setInsights(result)
        setStatus("success")
      })
      .catch(() => setStatus("error"))
  }, [api])

  useEffect(() => {
    load()
  }, [load])

  return (
    /* T21 (ADR-070), founder review 2026-09-10: ONE ANSWER, ONE PROOF, ONE
       DISCLOSURE. The realized figure with its Estimated marker is the answer
       and adherence is its clause; the weigh-in trend — the only real series
       the product owns — is the proof beneath it; the forecast-against-goal
       and "actual, not tracked yet" move behind a disclosure, because budget
       accounting is supporting intelligence, not the question someone opens
       Insights with. Same numbers, same honesty labels, nothing computed here. */
    <section aria-labelledby="insights-heading" className="flex flex-col gap-6">
      <h1 id="insights-heading" className="font-display text-3xl font-bold tracking-tight text-ink">
        {t("insights.title")}
      </h1>

      {status === "loading" && <LoadingState />}
      {status === "error" && <ErrorState onRetry={load} />}
      {status === "success" && <InsightsHeadline insights={insights} />}

      <ProgressSection />

      <details className="group rounded-bb-xl border border-line bg-paper p-4">
        <summary className="min-h-11 cursor-pointer list-none text-sm font-semibold text-ink marker:content-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green">
          <span className="flex items-center justify-between gap-3">
            <span>{t("views.budget.spendingDisclosure")}</span>
            <span aria-hidden="true" className="shrink-0 text-ink-2 group-open:rotate-45">
              +
            </span>
          </span>
        </summary>
        {/* Budget Model v2: forecast (estimate) vs actual (not tracked yet). */}
        <div className="mt-3 flex flex-col gap-4">
          <InsightsDetail insights={insights} />
          <BudgetForecastBlock />
        </div>
      </details>
    </section>
  )
}
